import React, { useState } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Loader2, Sparkles, CheckCircle, AlertTriangle, Target, ListChecks } from "lucide-react";

export function ExtractObligationsButton({ eventType, eventId, contractId, label, size = "sm", className = "" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState({});
  const [saving, setSaving] = useState(false);

  const handleExtract = async () => {
    setLoading(true);
    setResults(null);
    try {
      const res = await apiClient.post("/ai/extract-obligations-from-event", {
        event_type: eventType,
        event_id: eventId,
        contract_id: contractId,
      });
      setResults(res.data);
      const sel = {};
      res.data.obligations?.forEach(o => { sel[o.id] = true; });
      res.data.milestones?.forEach(m => { sel[m.id] = true; });
      setSelected(sel);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI extraction failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const obligations = (results?.obligations || [])
        .filter(o => selected[o.id])
        .map(o => ({
          contract_id: contractId,
          title: o.title,
          description: o.description || "",
          responsible_party: o.responsible_party || "internal",
          due_date: o.suggested_due_date || o.due_date_override || new Date(Date.now() + 30*24*60*60*1000).toISOString().split("T")[0],
          priority: o.priority || "medium",
          source: `ai_extracted_${eventType}`,
          notes: o.source_context || "",
        }));

      const milestones = (results?.milestones || [])
        .filter(m => selected[m.id])
        .map(m => ({
          contract_id: contractId,
          title: m.title,
          description: m.description || "",
          due_date: m.suggested_due_date || m.due_date_override || new Date(Date.now() + 30*24*60*60*1000).toISOString().split("T")[0],
          source: `ai_extracted_${eventType}`,
        }));

      let totalCreated = 0;
      if (obligations.length > 0) {
        await apiClient.post("/obligations/bulk", obligations);
        totalCreated += obligations.length;
      }
      if (milestones.length > 0) {
        await apiClient.post("/milestones/bulk", milestones);
        totalCreated += milestones.length;
      }

      toast.success(`${totalCreated} items added to compliance tracker`);
      setOpen(false);
      setResults(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const totalItems = (results?.obligations?.length || 0) + (results?.milestones?.length || 0);
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <>
      <Button
        variant="outline"
        size={size}
        className={`gap-1.5 rounded-sm ${className}`}
        onClick={() => { setOpen(true); if (!results) handleExtract(); }}
        data-testid={`extract-obligations-${eventType}-${eventId}`}
      >
        <Sparkles size={12} className="text-amber-500" />
        {label || "AI Extract Obligations"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles size={18} className="text-amber-500" />
              AI-Suggested Obligations & Milestones
            </DialogTitle>
            <DialogDescription>
              {results ? `Found ${totalItems} items from this ${eventType.replace("_", " ")}. Select which to add to the compliance tracker.` : "Analyzing content..."}
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="flex flex-col items-center py-12 gap-3">
              <Loader2 size={32} className="animate-spin text-amber-500" />
              <p className="text-sm text-slate-500">AI is analyzing the {eventType.replace("_", " ")}...</p>
            </div>
          )}

          {!loading && results && totalItems === 0 && (
            <div className="flex flex-col items-center py-12 gap-3">
              <CheckCircle size={32} className="text-emerald-500" />
              <p className="text-sm text-slate-500">No new obligations or milestones were identified in this {eventType.replace("_", " ")}.</p>
            </div>
          )}

          {!loading && results && totalItems > 0 && (
            <div className="space-y-4">
              {results.obligations?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <ListChecks size={14} className="text-blue-600" />
                    <span className="text-sm font-semibold text-slate-700">Obligations ({results.obligations.length})</span>
                  </div>
                  <div className="space-y-2">
                    {results.obligations.map(o => (
                      <ObligationItem
                        key={o.id}
                        item={o}
                        checked={!!selected[o.id]}
                        onToggle={() => setSelected(s => ({ ...s, [o.id]: !s[o.id] }))}
                        onDateChange={(d) => { o.due_date_override = d; }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {results.milestones?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Target size={14} className="text-emerald-600" />
                    <span className="text-sm font-semibold text-slate-700">Milestones ({results.milestones.length})</span>
                  </div>
                  <div className="space-y-2">
                    {results.milestones.map(m => (
                      <ObligationItem
                        key={m.id}
                        item={m}
                        checked={!!selected[m.id]}
                        onToggle={() => setSelected(s => ({ ...s, [m.id]: !s[m.id] }))}
                        onDateChange={(d) => { m.due_date_override = d; }}
                        isMilestone
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {!loading && results && totalItems > 0 && (
              <>
                <p className="text-xs text-slate-400 mr-auto">{selectedCount} of {totalItems} selected</p>
                <Button variant="outline" onClick={handleExtract} className="rounded-sm gap-1 text-xs" data-testid="re-extract-btn">
                  <Sparkles size={12} /> Re-analyze
                </Button>
                <Button onClick={handleSave} disabled={saving || selectedCount === 0} className="rounded-sm gap-1" data-testid="save-extracted-obligations-btn">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                  Add {selectedCount} to Tracker
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ObligationItem({ item, checked, onToggle, onDateChange, isMilestone }) {
  const [dueDate, setDueDate] = useState(item.suggested_due_date || "");

  const priorityColors = {
    low: "bg-slate-100 text-slate-600",
    medium: "bg-blue-100 text-blue-600",
    high: "bg-amber-100 text-amber-700",
    critical: "bg-red-100 text-red-700",
  };

  return (
    <div className={`flex gap-3 p-3 border rounded-sm ${checked ? "bg-white border-slate-300" : "bg-slate-50/50 border-slate-200 opacity-60"}`} data-testid={`extracted-item-${item.id}`}>
      <Checkbox checked={checked} onCheckedChange={onToggle} className="mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-medium text-slate-800">{item.title}</span>
          {!isMilestone && item.priority && (
            <Badge className={`rounded-sm text-[9px] ${priorityColors[item.priority] || priorityColors.medium}`}>{item.priority}</Badge>
          )}
          {!isMilestone && item.responsible_party && (
            <Badge variant="outline" className="rounded-sm text-[9px]">{item.responsible_party}</Badge>
          )}
          {isMilestone && <Badge className="rounded-sm text-[9px] bg-emerald-100 text-emerald-700">milestone</Badge>}
        </div>
        {item.description && <p className="text-xs text-slate-500 mb-1">{item.description}</p>}
        {item.source_context && (
          <p className="text-[11px] text-slate-400 italic mb-1.5">Source: "{item.source_context}"</p>
        )}
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">Due:</span>
          <Input
            type="date"
            value={dueDate}
            onChange={e => { setDueDate(e.target.value); onDateChange(e.target.value); }}
            className="h-6 text-[11px] w-36 rounded-sm"
          />
          {!dueDate && <AlertTriangle size={10} className="text-amber-500" />}
        </div>
      </div>
    </div>
  );
}
